import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth, IS_MOCK } from './useAuth';
import type { AuthAccount } from './useAuth';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  isAccessDenied: boolean;
  account: AuthAccount | null;
  login: () => Promise<void>;
  logout: () => void | Promise<void>;
  getAccessToken: () => string | Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const [enrichedAccount, setEnrichedAccount] = useState<AuthAccount | null>(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      setEnrichedAccount(null);
      setIsAccessDenied(false);
      fetchedRef.current = false;
      return;
    }

    if (fetchedRef.current) return;
    fetchedRef.current = true;

    if (IS_MOCK) {
      // In mock mode the account already has the correct role from sessionStorage
      setEnrichedAccount(auth.account);
    } else {
      // In real mode, enrich the account with DB role info from /api/auth/me
      apiClient
        .get<AuthAccount>(endpoints.auth.me)
        .then((res) => {
          setEnrichedAccount({ ...auth.account, ...res.data } as AuthAccount);
        })
        .catch((error) => {
          if (error.response?.status === 403) {
            // User is authenticated with Azure AD but not in AllowedUser table
            setIsAccessDenied(true);
          } else {
            // Other errors: use the MSAL account as-is
            setEnrichedAccount(auth.account);
          }
        });
    }
  }, [auth.isAuthenticated, auth.account]);

  const value: AuthContextValue = {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    isAccessDenied,
    account: enrichedAccount ?? auth.account,
    login: auth.login,
    logout: auth.logout,
    getAccessToken: auth.getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
